export function setURLParam(window, param: string, value: string) {
  const url = new URL(window.location.href);
  const oldParam = url.searchParams.get(param);
  if (!oldParam || oldParam !== value) {
    url.searchParams.set(param, value);
    window.history.replaceState(null, null, url);
  }
}

export function getURLParam(window, param: string) {
  const url = new URL(window.location.href);
  return url.searchParams.get(param);
}

export function removeURLParam(window, param: string) {
  const url = new URL(window.location.href);
  const paramValue = url.searchParams.get(param);
  if (paramValue) {
    url.searchParams.delete(param);
    window.history.replaceState(null, null, url);
  }
}

/**
 * Parses CodeCanvas repository URLs and extracts GitHub repository information
 * Supports URLs like:
 * - https://code-canvas.com/owner/repo
 * - https://www.code-canvas.com/owner/repo
 * - https://dev.code-canvas.com/owner/repo
 * - http://localhost:3001/owner/repo
 * - https://code-canvas.com/owner/repo/tree/branch
 * - https://www.code-canvas.com/owner/repo/tree/branch
 * - https://dev.code-canvas.com/owner/repo/tree/branch
 *
 * @param url - The URL to parse
 * @returns Object containing owner, repo, branch (optional), and the equivalent GitHub URL, or null if not a CodeCanvas repo URL
 */
export function parseCodeCanvasRepoURL(url: string): {
  owner: string;
  repo: string;
  branch?: string;
  githubURL: string;
} | null {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();
    const pathname = urlObj.pathname;

    // Check if hostname matches CodeCanvas domains
    const isCodeCanvasDomain =
      hostname === "code-canvas.com" ||
      hostname === "www.code-canvas.com" ||
      hostname === "dev.code-canvas.com" ||
      hostname === "localhost";

    if (!isCodeCanvasDomain) {
      return null;
    }

    // Parse pathname to extract owner/repo/branch
    // Expected formats:
    // /owner/repo
    // /owner/repo/tree/branch
    const pathSegments = pathname
      .split("/")
      .filter((segment) => segment.length > 0);

    if (pathSegments.length < 2) {
      return null;
    }

    const owner = pathSegments[0];
    const repo = pathSegments[1];

    // Validate owner and repo (basic validation)
    if (!owner || !repo || owner.includes(".") || repo.includes("..")) {
      return null;
    }

    // Check for branch in the path
    let branch: string | undefined;
    if (pathSegments.length >= 4 && pathSegments[2] === "tree") {
      branch = pathSegments[3];
    }

    // Construct the equivalent GitHub URL
    let githubURL = `https://github.com/${owner}/${repo}`;
    if (branch) {
      githubURL += `/tree/${branch}`;
    }

    return {
      owner,
      repo,
      branch,
      githubURL,
    };
  } catch (error) {
    // Invalid URL format
    return null;
  }
}

/**
 * Checks if the current window location is a CodeCanvas repository URL
 * @param window - The window object
 * @returns Parsed repository information or null
 */
export function parseCurrentCodeCanvasRepoURL(window: Window): {
  owner: string;
  repo: string;
  branch?: string;
  githubURL: string;
} | null {
  return parseCodeCanvasRepoURL(window.location.href);
}
